import { Injectable } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';
import { WebService } from './web.service';

export interface LedgerRoles {
  viewer: boolean;
  preparer: boolean;
  approver: boolean;
  periodOwner: boolean;
}

export interface LedgerMe {
  roles: LedgerRoles;
  userGuid: string;
  allowSelfApproval: boolean;
}

// placeholder groups, kept in step with LEDGER_*_GROUPS defaults in api/routes/ledger.js
export const LEDGER_VIEWER_GROUPS = ['Executive', 'System Admins'];

export interface LedgerInquiryFilters {
  FromDate?: string | null;
  ThroughDate?: string | null;
  AccountID?: number | string | null;
  AccountTypeID?: number | string | null;
  SourceType?: string | null;
  PaymentMethodID?: number | string | null;
  CompanyID?: number | string | null;
  Reference?: string | null;
  SearchText?: string | null;
  Status?: string | null;
  DateNeedsReview?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class LedgerService {
  private roles$: Observable<LedgerMe> | null = null;

  constructor(private web: WebService) { }

  // the server decides permissions; the screen only uses this to show or hide buttons
  getMyRoles(): Observable<LedgerMe> {
    if (!this.roles$) this.roles$ = this.web.getWebCall('ledger', 'me').pipe(shareReplay(1));
    return this.roles$;
  }

  getPeriodHistory(periodId: number): Observable<any> {
    return this.web.getWebCall('ledger', `periods/${periodId}/history`);
  }

  setPeriodStatus(periodId: number, status: 'Open' | 'Closed', reason: string): Observable<any> {
    return this.web.getWebCall('ledger', `periods/${periodId}/status`, { body: { Status: status, Reason: reason } });
  }

  getFilterOptions(): Observable<any> {
    return this.web.getWebCall('ledger', 'filter-options');
  }

  getInquiry(filters: LedgerInquiryFilters): Observable<any> {
    return this.web.getWebCall('ledger', 'inquiry', { params: this.toParams(filters) });
  }

  getTransactionDetail(transactionId: number): Observable<any> {
    return this.web.getWebCall('ledger', `transactions/${transactionId}`);
  }

  getUnresolvedSummary(): Observable<any> {
    return this.web.getWebCall('ledger', 'unresolved-summary');
  }

  getPeriods(): Observable<any> {
    return this.web.getWebCall('ledger', 'periods');
  }

  getJournals(filters: { Status?: string; PeriodID?: number | string; SearchText?: string }): Observable<any> {
    return this.web.getWebCall('ledger', 'journals', { params: this.toParams(filters) });
  }

  getJournal(journalEntryId: number): Observable<any> {
    return this.web.getWebCall('ledger', `journals/${journalEntryId}`);
  }

  saveJournal(journal: any): Observable<any> {
    return this.web.getWebCall('ledger', 'journals/save', { body: journal });
  }

  journalAction(journalEntryId: number, action: JournalAction, rowVer: string, reason?: string): Observable<any> {
    return this.web.getWebCall('ledger', `journals/${journalEntryId}/${action}`, {
      body: { RowVer: rowVer, Reason: reason ?? null }
    });
  }

  postJournal(journalEntryId: number, rowVer: string): Observable<any> {
    return this.web.getWebCall('ledger', `journals/${journalEntryId}/post`, { body: { RowVer: rowVer } });
  }

  reverseJournal(journalEntryId: number, rowVer: string, reason: string, reversalDate: string, createReplacement: boolean): Observable<any> {
    return this.web.getWebCall('ledger', `journals/${journalEntryId}/reverse`, {
      body: { RowVer: rowVer, Reason: reason, ReversalDate: reversalDate, CreateReplacement: createReplacement }
    });
  }

  getRecycleBin(filters: { DeletedFrom?: string; DeletedThrough?: string; SearchText?: string }): Observable<any> {
    return this.web.getWebCall('ledger', 'journals/recycle-bin', { params: this.toParams(filters) });
  }

  getDeletedHistory(): Observable<any> {
    return this.web.getWebCall('ledger', 'deleted-history');
  }

  private toParams(filters: Record<string, any>) {
    return Object.entries(filters)
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .map(([name, value]) => ({ name, value }));
  }
}

export type JournalAction = 'submit' | 'return' | 'cancel' | 'delete' | 'restore';
